import { useEffect, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { ApiError } from "../lib/api.ts";

/**
 * Watches the TanStack Query cache for the cold-core "waking" condition so the shell can show the
 * banner (design 12.3, NFR-7 AC1). The core is considered waking when any query currently carries a
 * `waking` {@link ApiError} — i.e. a 503 from the BFF — and that query is not presently succeeding.
 *
 * This keeps the signal honest: the banner appears while a real call is failing with the waking
 * state and clears as soon as the retry (configured in `query-client.ts`) succeeds. It does not
 * fabricate a cold state when there are simply no queries yet (the shell loading with nothing to
 * fetch is NOT "waking").
 *
 * `retry` refetches every query so an operator who clicks "Retry now" does not wait for the backoff.
 */
export interface ColdCore {
  readonly waking: boolean;
  readonly retrying: boolean;
  retry(): void;
}

export function useColdCore(): ColdCore {
  const client = useQueryClient();
  const [waking, setWaking] = useState(false);
  const [retrying, setRetrying] = useState(false);

  useEffect(() => {
    const cache = client.getQueryCache();
    const compute = (): void => {
      const anyWaking = cache.getAll().some((query) => {
        const error = query.state.error;
        return error instanceof ApiError && error.kind === "waking";
      });
      setWaking(anyWaking);
    };
    compute();
    const unsubscribe = cache.subscribe(compute);
    return unsubscribe;
  }, [client]);

  const retry = (): void => {
    setRetrying(true);
    void client
      .refetchQueries()
      .finally(() => setRetrying(false));
  };

  return { waking, retrying, retry };
}
